import Cookies from 'js-cookie';
import { DOMAIN, ENVIRONMENT } from 'settings';
import { notifyPresenceLogout } from './presence';
import { currentLogin } from './loginProtocol';

const logoutKey = `pm_${ENVIRONMENT}_logout`;
let localMarker: string | null = null;

export function getLogoutMarker(): string | null {
  try {
    return localMarker || window.localStorage.getItem(logoutKey);
  } catch {
    return localMarker;
  }
}

export function logoutAllTabs(): void {
  notifyPresenceLogout();
  // Invalidate outstanding refreshes before removing the shared credentials.
  localMarker = `${Date.now()}:${Math.random()}`;
  try {
    window.localStorage.setItem(logoutKey, localMarker);
  } catch {
    // Storage restrictions must never prevent logout in this tab.
  }
  for (const name of [
    'token',
    'token_exp',
    'refresh',
    'refresh_exp',
    'soundEnabled',
    'person',
  ]) {
    Cookies.remove(`pm_${ENVIRONMENT}_${name}`, { domain: DOMAIN });
  }
  // A new document also discards global notifications, sockets and legacy state.
  window.location.replace('/auth/login');
}

export function redirectLegacyLogin(): boolean {
  const access = Cookies.get(`pm_${ENVIRONMENT}_token`);
  const refresh = Cookies.get(`pm_${ENVIRONMENT}_refresh`);
  if (
    (access && !currentLogin(access)) ||
    (refresh && !currentLogin(refresh))
  ) {
    logoutAllTabs();
    return true;
  }
  return false;
}

export function rejectBrowserLogin(token?: string): void {
  // A delayed API response/socket close must not sign out a newer login.
  if (token && Cookies.get(`pm_${ENVIRONMENT}_token`) === token)
    logoutAllTabs();
}

export function startLogoutSync(): () => void {
  let observed = getLogoutMarker();
  let redirecting = false;
  const check = () => {
    if (redirecting) return;
    if (redirectLegacyLogin()) {
      redirecting = true;
      return;
    }
    const current = getLogoutMarker();
    if (!current || current === observed || redirecting) return;
    observed = current;
    redirecting = true;
    // Do not rebroadcast or clear cookies: a newer login may already exist.
    window.location.replace('/auth/login');
  };
  const onStorage = (event: StorageEvent) => {
    if (event.key === logoutKey) check();
  };
  const onVisibility = () => {
    if (document.visibilityState === 'visible') check();
  };
  window.addEventListener('storage', onStorage);
  window.addEventListener('focus', check);
  window.addEventListener('pageshow', check);
  document.addEventListener('visibilitychange', onVisibility);
  return () => {
    window.removeEventListener('storage', onStorage);
    window.removeEventListener('focus', check);
    window.removeEventListener('pageshow', check);
    document.removeEventListener('visibilitychange', onVisibility);
  };
}
